import React, { useState, useEffect } from 'react';
import { shareContentLead } from '../../services/leadService';
import PopupComponent from '../../components/PopupComponent';
import logger from '../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import {
  fetchAllContent,
  fetchWhatsAppTemplates,
  clearContentErrors,
  selectAllContent,
  selectWhatsAppTemplates,
  selectContentLoading,
  selectContentErrors,
  selectContentCounts,
  selectFilteredContent,
  ContentItem,
  WhatsAppTemplate
} from '../../store/slices/contentSlice';

interface Lead {
  _id: string;
  name?: string;
  phone?: string;
  [key: string]: any;
}

interface WhatsAppContentSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onContentSelect: (processedMessage: string) => void;
  currentLead: Lead;
}

const WhatsAppContentSelector: React.FC<WhatsAppContentSelectorProps> = ({
  isOpen,
  onClose,
  onContentSelect,
  currentLead,
}) => {
  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const allContent = useAppSelector(selectAllContent);
  const whatsAppTemplates = useAppSelector(selectWhatsAppTemplates);
  const loading = useAppSelector(selectContentLoading);
  const errors = useAppSelector(selectContentErrors);
  const counts = useAppSelector(selectContentCounts);

  // Local state for UI only
  const [selectedContent, setSelectedContent] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'file' | 'page' | 'message' | 'whatsapp_template'>('file');
  const [searchInput, setSearchInput] = useState<string>('');

  // Get filtered content using selector
  const filteredContent = useAppSelector(selectFilteredContent(activeTab, searchInput));

  logger.log('🎯 WhatsAppContentSelector: Redux state loaded', {
    allContentCount: allContent.length,
    templatesCount: whatsAppTemplates.length,
    loading,
    counts,
    currentLead
  });

  // Load content when component opens
  useEffect(() => {
    if (isOpen) {
      logger.log('📥 Content Selector opened, dispatching content fetch...');
      dispatch(fetchAllContent({}));
    }
  }, [isOpen, dispatch]);

  const handleTabChange = async (newTab: typeof activeTab) => {
    logger.log('🔄 Tab changed to:', newTab);
    setActiveTab(newTab);
    setSelectedContent(null);
    
    // Load WhatsApp templates if tab selected
    if (newTab === 'whatsapp_template') {
      logger.log('📥 Loading WhatsApp templates via Redux...');
      dispatch(fetchWhatsAppTemplates({}));
    }
  };

  const processSelectedContent = async (content: ContentItem | WhatsAppTemplate): Promise<string> => {
    logger.log('⚙️ Processing content:', content);
    
    try {
      const leadName = currentLead.name || 'Customer';
      
      if (content.type === 'file' || content.type === 'page') {
        logger.log('🔗 Generating unique link for:', content.type);
        
        const body = {
          contentId: content._id,
          leadIds: [currentLead._id],
          performedAt: new Date(),
          contentType: content.type,
        };
        
        const response = await shareContentLead(body);
        
        if (!response?.data?.data?.uniqueLink) {
          throw new Error('No unique link generated');
        }
        
        const processedMessage = `Hi ${leadName},

Here's the link to view:
${response.data.data.uniqueLink}`;
        
        logger.log('✅ Generated link message:', processedMessage);
        return processedMessage;
        
      } else if (content.type === 'message') {
        logger.log('💬 Processing message content with personalization');
        
        let messageContent = '';
        const contentItem = content as ContentItem;
        if (contentItem.details?.message) {
          messageContent = contentItem.details.message;
        } else if (contentItem.details?.description) {
          messageContent = contentItem.details.description;
        }
        
        const processedMessage = messageContent.replace(/@lead name/g, leadName);
        logger.log('✅ Personalized message:', processedMessage);
        return processedMessage;
        
      } else if (content.type === 'APPROVED' || activeTab === 'whatsapp_template') {
        logger.log('📋 Processing WhatsApp template');
        
        const template = content as WhatsAppTemplate;
        const bodyComponent = template.components?.find(c => c.type === 'BODY');
        let templateText = bodyComponent?.text || '';
        
        templateText = templateText.replace(/\{\{1\}\}/g, leadName);
        
        logger.log('✅ Processed WhatsApp template:', templateText);
        return templateText;
      }
      
      logger.log('⚠️ Unknown content type:', content.type);
      return 'Content selected';
      
    } catch (error) {
      logger.log('❌ Error processing content:', error);
      throw error;
    }
  };

  const handleContentSelect = async () => {
    if (!selectedContent) {
      logger.log('⚠️ No content selected');
      return;
    }
    
    try {
      logger.log('🎯 Handling content selection:', selectedContent);
      
      const content = filteredContent.find(item => item._id === selectedContent);
      if (!content) {
        logger.log('❌ Selected content not found:', selectedContent);
        return;
      }
      
      const processedMessage = await processSelectedContent(content);
      logger.log('📤 Sending processed message to parent:', processedMessage);
      
      onContentSelect(processedMessage);
      onClose();
      
    } catch (error) {
      logger.log('❌ Error selecting content:', error);
    }
  };

  const handleContentClick = (contentId: string) => {
    logger.log('👆 Content clicked:', contentId);
    setSelectedContent(contentId);
  };

  const renderContentPreview = (content: ContentItem | WhatsAppTemplate) => {
    if (activeTab === 'whatsapp_template') {
      const template = content as WhatsAppTemplate;
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      return bodyComponent?.text || 'No preview available';
    }
    
    const contentItem = content as ContentItem;
    if (contentItem.type === 'file' && contentItem.files && contentItem.files.length > 0) {
      return `File: ${contentItem.files[0].fileName}`;
    }
    
    return contentItem.details?.description || 
           contentItem.details?.message || 
           'No preview available';
  };

  const renderContentCard = (content: ContentItem | WhatsAppTemplate) => {
    const isSelected = content._id === selectedContent;
    const title = content.name || (content as ContentItem).details?.title || 'Untitled';
    const preview = renderContentPreview(content);
    
    return (
      <div
        key={content._id}
        onClick={() => handleContentClick(content._id)}
        style={{
          padding: '16px',
          border: `2px solid ${isSelected ? '#3FAEFD' : '#E5E7EB'}`,
          borderRadius: '8px',
          backgroundColor: isSelected ? '#F0F9FF' : 'white',
          cursor: 'pointer',
          height: '200px',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease',
          boxShadow: isSelected ? '0 4px 8px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#1F2937',
          marginBottom: '8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {title}
        </div>
        
        <div style={{
          fontSize: '12px',
          color: '#059669',
          marginBottom: '8px',
          textTransform: 'uppercase',
          fontWeight: 500
        }}>
          {activeTab === 'whatsapp_template' ? 'WhatsApp Template' : content.type}
        </div>
        
        <div style={{
          fontSize: '13px',
          color: '#6B7280',
          lineHeight: '1.4',
          overflow: 'hidden',
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: 6,
          WebkitBoxOrient: 'vertical'
        }}>
          {preview}
        </div>
      </div>
    );
  };

  const tabButtonStyle = (isActive: boolean) => ({
    padding: '8px 16px',
    borderRadius: '6px 6px 0 0',
    border: 'none',
    borderBottom: isActive ? '2px solid #3FAEFD' : '2px solid transparent',
    backgroundColor: isActive ? '#F0F9FF' : 'transparent',
    color: isActive ? '#3FAEFD' : '#6B7280',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  const buttonStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px',
    fontWeight: 600,
  } as const;

  const primaryButtonStyle = (disabled: boolean) => ({
    ...buttonStyle,
    backgroundColor: disabled ? '#D1D5DB' : '#3FAEFD',
    color: 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: '#6B7280',
  };

  const renderFooter = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <button
        onClick={onClose}
        style={secondaryButtonStyle}
      >
        Cancel
      </button>
      <button
        onClick={handleContentSelect}
        disabled={!selectedContent || loading.content || loading.templates}
        style={primaryButtonStyle(!selectedContent || loading.content || loading.templates)}
      >
        {(loading.content || loading.templates) ? 'Processing...' : 'Select Content'}
      </button>
    </div>
  );

  const renderEmptyState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 0',
      color: '#6B7280',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px', color: '#D1D5DB' }}>
        📁
      </div>
      <h4 style={{ marginBottom: '8px', color: '#4B5563' }}>No content found</h4>
      <p>No {activeTab} content available or matches your search.</p>
    </div>
  );

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title={`WhatsApp Content (${counts.file + counts.page + counts.message + counts.whatsapp_template})`}
      size="lg"
      footer={renderFooter()}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '600px' }}>
        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #E5E7EB',
          marginBottom: '16px'
        }}>
          <button 
            onClick={() => handleTabChange('file')}
            style={tabButtonStyle(activeTab === 'file')}
          >
            Files ({counts.file})
          </button>
          <button 
            onClick={() => handleTabChange('page')}
            style={tabButtonStyle(activeTab === 'page')}
          >
            Pages ({counts.page})
          </button>
          <button 
            onClick={() => handleTabChange('message')}
            style={tabButtonStyle(activeTab === 'message')}
          >
            Messages ({counts.message})
          </button>
          <button 
            onClick={() => handleTabChange('whatsapp_template')}
            style={tabButtonStyle(activeTab === 'whatsapp_template')}
          >
            WhatsApp Templates ({counts.whatsapp_template})
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #E5E7EB',
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>

        {/* Error state */}
        {(errors.content || errors.templates) && (
          <div style={{
            color: '#EF4444',
            textAlign: 'center',
            margin: '16px 0',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {errors.content && `Content: ${errors.content}`}
            {errors.content && errors.templates && <br />}
            {errors.templates && `Templates: ${errors.templates}`}
            <button
              onClick={() => {
                dispatch(fetchAllContent({ forceRefresh: true }));
                if (activeTab === 'whatsapp_template') {
                  dispatch(fetchWhatsAppTemplates({ forceRefresh: true }));
                }
              }}
              style={{
                display: 'block',
                margin: '8px auto 0',
                background: 'none',
                border: 'none',
                color: '#2563EB',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {(loading.content || loading.templates) ? (
          <div style={{ 
            padding: '40px 0',
            textAlign: 'center',
            color: '#6B7280',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1
          }}>
            <div style={{ marginBottom: '16px' }}>
              <i className="ri-loader-4-line" style={{ 
                fontSize: '32px', 
                animation: 'spin 1s linear infinite'
              }}></i>
            </div>
            <div>Loading...</div>
          </div>
        ) : (
          /* Content Grid */
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '4px'
          }}>
            {filteredContent.length > 0 ? (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '16px',
                padding: '8px',
              }}>
                {filteredContent.map(renderContentCard)}
              </div>
            ) : (
              renderEmptyState()
            )}
          </div>
        )}
      </div>
    </PopupComponent>
  );
};

export default WhatsAppContentSelector;